import { useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock, FileText, Sun, Tag, Trash2, UserRound } from 'lucide-react';
import { NotionDrawer } from '../ui/notion/NotionDrawer';
import { NotionButton } from '../ui/notion';
import { PropRow } from '../shared/activity-drawer/ui/PropRow';
import { PropTextRow, PropToggleRow, UserSelectRow } from '../shared/property-rows';
import { useApp } from '../AppProvider';
import { useAuth, useUserData } from '../../lib/auth';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { supabase } from '../../lib/supabase';
import { useCalendarSharingStore } from '../../lib/stores/calendarSharingStore';
import { useCalendarUnavailabilityStore } from '../../lib/stores/calendarUnavailabilityStore';
import { calendarOwnerOf } from '../../lib/calendar/sharing';
import { useEnsureSharing } from '../../lib/calendar/team/useEnsureSharing';
import { PersonPausesEditor } from './PersonPausesEditor';
import {
  canManageUnavailability, formForNew, formFromPeriod, periodFromForm, UNAVAILABLE_TITLE,
} from '../../lib/calendar/team/unavailability';

/** Cosa aprire: una riga esistente (id) oppure una nuova, con persona e orario di partenza se scelti sul calendario. */
export interface UnavailabilityTarget { id?: string | null; userId?: string | null; start?: Date | null; end?: Date | null; allDay?: boolean }

const ICON = 'h-3 w-3';
const dateInputCls = 'bg-transparent text-[13px] text-[#37352F] dark:text-white focus:outline-none [color-scheme:light] dark:[color-scheme:dark]';

/** Chi sono, se posso segnare sui colleghi e la riga aperta (se esiste), con la sola lettura che ne segue. */
function useUnavailabilityAccess(target: UnavailabilityTarget | null) {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const existing = useCalendarUnavailabilityStore(s => (target?.id ? s.rows[target.id] : undefined));
  const meId = user?.id ?? null;
  const canScheduleOthers = hasPermission('calendar_others', 'schedule');
  const readOnly = !!existing && !canManageUnavailability(existing.user_id, meId, canScheduleOthers);
  return { meId, canScheduleOthers, existing, readOnly };
}

interface FormProps {
  target: UnavailabilityTarget;
  /** id del <form>: il pulsante di salvataggio sta nel piè del drawer che lo contiene (type="submit" form={formId}) */
  formId: string;
  onDone: () => void;
  onSavingChange?: (saving: boolean) => void;
  /** Persona scelta fuori dal modulo ("Calendario di" in cima a "Nuovo Evento"): il modulo non mostra la sua riga */
  personId?: string;
}

/**
 * Il modulo dell'indisponibilità, senza drawer intorno: lo usano il drawer qui sotto (aprire o segnare da "⋯",
 * dalla vista Team) e "Nuovo Evento" nel tab "Non disponibile". Sul proprio calendario lo può fare chiunque; su
 * quello dei colleghi serve "Segna le indisponibilità dei colleghi".
 */
export function UnavailabilityForm({ target, formId, onDone, onSavingChange, personId: chosenPersonId }: FormProps) {
  // Le persone dell'azienda servono a "Calendario di": fuori dalla pagina Calendario possono non essere caricate
  useEnsureSharing();
  const { organizationId } = useUserData();
  const { toast } = useApp();
  const people = useCalendarSharingStore(s => s.people);
  const save = useCalendarUnavailabilityStore(s => s.save);
  const { meId, canScheduleOthers, existing, readOnly } = useUnavailabilityAccess(target);

  const [personId, setPersonId] = useState('');
  const [title, setTitle] = useState(UNAVAILABLE_TITLE);
  const [note, setNote] = useState('');
  const [form, setForm] = useState(() => formForNew());
  const [error, setError] = useState<string | null>(null);
  const [clashes, setClashes] = useState(0);

  // Riparte da capo a ogni apertura: la riga esistente, oppure persona e orario scelti sul calendario
  useEffect(() => {
    setError(null);
    if (existing) {
      setPersonId(existing.user_id);
      setTitle(existing.title);
      setNote(existing.note || '');
      setForm(formFromPeriod(existing));
    } else {
      const wanted = target.userId && canManageUnavailability(target.userId, meId, canScheduleOthers) ? target.userId : meId;
      setPersonId(chosenPersonId || wanted || '');
      setTitle(UNAVAILABLE_TITLE);
      setNote('');
      setForm(formForNew(target.start, target.end, target.allDay));
    }
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (chosenPersonId && !existing) setPersonId(chosenPersonId); }, [chosenPersonId, existing]);

  const users = useMemo(() => {
    const list = people.filter(p => canManageUnavailability(p.id, meId, canScheduleOthers)).map(p => ({ id: p.id, full_name: p.name }));
    // La persona di una riga esistente resta nell'elenco anche se non si puo' scegliere (sola lettura, utente disattivato)
    if (personId && !list.some(u => u.id === personId)) list.push({ id: personId, full_name: people.find(p => p.id === personId)?.name || 'Utente non più attivo' });
    return list;
  }, [people, meId, canScheduleOthers, personId]);
  const personName = users.find(u => u.id === personId)?.full_name || 'questa persona';
  const period = periodFromForm(form);

  // Appuntamenti gia' fissati nel periodo: restano dove sono, ma chi segna l'indisponibilita' deve saperlo
  const periodKey = 'error' in period ? '' : `${period.start_time}|${period.end_time}`;
  useEffect(() => {
    setClashes(0);
    if (!personId || !periodKey) return;
    const [from, to] = periodKey.split('|');
    let off = false;
    void supabase
      .from('sales_activities')
      .select('id, status, assigned_to, user_id')
      .or(`assigned_to.eq.${personId},and(assigned_to.is.null,user_id.eq.${personId})`)
      .gte('due_date', from)
      .lt('due_date', to)
      .then(({ data }) => {
        if (off) return;
        setClashes((data || []).filter((a: any) => a.status !== 'cancelled' && a.status !== 'completed' && calendarOwnerOf(a) === personId).length);
      });
    return () => { off = true; };
  }, [personId, periodKey]);

  const patch = (p: Partial<typeof form>) => setForm(f => ({ ...f, ...p }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!organizationId || !personId) { setError('Scegli la persona'); return; }
    if ('error' in period) { setError(period.error); return; }
    onSavingChange?.(true);
    const res = await save({
      id: existing?.id,
      organization_id: organizationId,
      user_id: personId,
      title: title.trim() || UNAVAILABLE_TITLE,
      note: note.trim() || null,
      ...period,
    });
    onSavingChange?.(false);
    if (!res.ok) { setError(res.error); return; }
    toast.success(existing ? 'Indisponibilità aggiornata' : 'Indisponibilità segnata');
    onDone();
  };

  return (
    <>
    <form id={formId} onSubmit={e => void onSubmit(e)}>
      <fieldset disabled={readOnly} className="space-y-0.5">
        {chosenPersonId === undefined && (
          <UserSelectRow
            icon={<UserRound className={ICON} strokeWidth={1.5} />}
            label="Calendario di"
            value={personId}
            onChange={setPersonId}
            users={users}
            disabled={readOnly || !!existing || users.length < 2}
          />
        )}
        {chosenPersonId === undefined && !existing && !canScheduleOthers && (
          <p className="px-1.5 pb-1 text-[11px] text-[#787774]">
            Puoi segnare solo sul tuo calendario. Per scegliere un collega serve il permesso "Segna le indisponibilità dei colleghi" sul tuo ruolo.
          </p>
        )}
        <PropTextRow icon={<Tag className={ICON} strokeWidth={1.5} />} label="Motivo" value={title} onChange={setTitle} placeholder="Es. Ferie, Fuori sede, Corso" />
        <PropToggleRow
          icon={<Sun className={ICON} strokeWidth={1.5} />}
          label="Tutto il giorno"
          checked={form.allDay}
          onChange={v => !readOnly && patch({ allDay: v })}
          onLabel="Sì, giornate intere"
          offLabel="No, con orari"
        />
        <PropRow icon={<CalendarDays className={ICON} strokeWidth={1.5} />} label={form.allDay ? 'Dal giorno' : 'Dal'}>
          <input type="date" aria-label="Dal giorno" value={form.fromDate} className={dateInputCls}
            onChange={e => patch({ fromDate: e.target.value, ...(e.target.value > form.toDate ? { toDate: e.target.value } : {}) })} />
          {!form.allDay && <input type="time" step={900} aria-label="Dalle" value={form.fromTime} className={dateInputCls} onChange={e => patch({ fromTime: e.target.value })} />}
        </PropRow>
        <PropRow icon={<Clock className={ICON} strokeWidth={1.5} />} label={form.allDay ? 'Al giorno' : 'Al'}>
          <input type="date" aria-label="Al giorno" value={form.toDate} min={form.fromDate} className={dateInputCls} onChange={e => patch({ toDate: e.target.value })} />
          {!form.allDay && <input type="time" step={900} aria-label="Alle" value={form.toTime} className={dateInputCls} onChange={e => patch({ toTime: e.target.value })} />}
        </PropRow>
        <PropTextRow icon={<FileText className={ICON} strokeWidth={1.5} />} label="Nota" value={note} onChange={setNote} placeholder="Facoltativa" />
      </fieldset>

      {readOnly ? (
        <p className="mt-3 px-1.5 text-[13px] text-[#787774]">Solo {personName} o chi ha il permesso può cambiarla.</p>
      ) : (
        <p className="mt-3 px-1.5 text-[13px] text-[#787774]">In questo periodo le operatrici vedono {personName === 'questa persona' ? 'la persona' : personName} come occupata e il modulo le avvisa se fissano un appuntamento.</p>
      )}
      {(error || ('error' in period && !readOnly)) && (
        <p className="mt-2 px-1.5 text-[13px] text-red-600">{error || ('error' in period ? period.error : '')}</p>
      )}
      {clashes > 0 && !readOnly && (
        <p className="mt-2 px-1.5 text-[13px] text-[#787774]">
          In questo periodo {personName} ha già {clashes === 1 ? 'un appuntamento' : `${clashes} appuntamenti`}: {clashes === 1 ? 'resta' : 'restano'} in calendario, spostali tu se serve.
        </p>
      )}
    </form>
    {/* Fuori dal <form>: Invio nel nome di una pausa non deve segnare l'indisponibilità */}
    {!existing && personId && (
      <PersonPausesEditor personId={personId} personName={personName} canEdit={canManageUnavailability(personId, meId, canScheduleOthers)} />
    )}
    </>
  );
}

interface DrawerProps { target: UnavailabilityTarget | null; onClose: () => void }

const DRAWER_FORM_ID = 'unavailability-drawer-form';

/** Drawer autonomo: apre un'indisponibilità esistente (calendario, vista Team) o ne segna una nuova dal menu "⋯". */
export function UnavailabilityDrawer({ target, onClose }: DrawerProps) {
  const { toast, confirm } = useApp();
  const remove = useCalendarUnavailabilityStore(s => s.remove);
  const people = useCalendarSharingStore(s => s.people);
  const { existing, readOnly } = useUnavailabilityAccess(target);
  const [saving, setSaving] = useState(false);

  if (!target) return null;

  const onDelete = async () => {
    if (!existing) return;
    const name = people.find(p => p.id === existing.user_id)?.name || 'La persona';
    const ok = await confirm({
      title: 'Cancella indisponibilità',
      message: `${name} tornerà disponibile in quel periodo.`,
      confirmText: 'Cancella',
      cancelText: 'Annulla',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    const res = await remove(existing.id);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success('Indisponibilità cancellata');
    onClose();
  };

  return (
    <NotionDrawer
      isOpen
      onClose={onClose}
      title={existing ? (readOnly ? 'Indisponibilità' : 'Modifica indisponibilità') : 'Non disponibile'}
      width="md"
      footer={readOnly ? undefined : (
        <div className="flex items-center gap-2">
          {existing && (
            <NotionButton variant="ghost" size="sm" icon={<Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />} onClick={() => void onDelete()}>Cancella</NotionButton>
          )}
          <span className="flex-1" />
          <NotionButton variant="ghost" size="sm" onClick={onClose}>Annulla</NotionButton>
          <NotionButton variant="primary" size="sm" type="submit" form={DRAWER_FORM_ID} loading={saving}>Salva</NotionButton>
        </div>
      )}
    >
      <UnavailabilityForm target={target} formId={DRAWER_FORM_ID} onDone={onClose} onSavingChange={setSaving} />
    </NotionDrawer>
  );
}
